import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bell, CheckCheck, Trash2, X, Inbox } from "lucide-react";
import { toast } from "sonner";
import { useRealtime } from "@/contexts/RealtimeContext";

const TYPE_TONE = {
  rfp_invite: "border-orange-500/40 text-orange-300",
  rfp_awarded: "border-emerald-500/40 text-emerald-300",
  rfp_lost: "border-white/10 text-zinc-400",
  vendor_review: "border-violet-500/40 text-violet-300",
  broadcast: "border-indigo-500/40 text-indigo-300",
  payment: "border-emerald-500/40 text-emerald-300",
  autopilot: "border-emerald-500/40 text-emerald-300",
  default: "border-white/10 text-zinc-400",
};

/**
 * NotificationDrawer — right-side sheet listing all notifications with
 * unread badges, "Mark all read", and per-item delete. Wired to the header
 * bell so alerts live inside the app itself (not just push).
 */
export function NotificationDrawer({ open, onOpenChange, onCountChange }) {
  const { subscribe } = useRealtime();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [tab, setTab] = useState("all");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/list", {
        params: { limit: 100, unread_only: tab === "unread" },
      });
      setItems(data.items || []);
      const s = await api.get("/notifications/summary");
      setUnread(s.data.unread || 0);
      onCountChange?.(s.data.unread || 0);
    } catch (e) { if (typeof console !== 'undefined') console.debug('components/NotificationDrawer.jsx: swallowed', e); }
  }, [tab, onCountChange]);

  useEffect(() => { if (open) load(); }, [open, load]);

  useEffect(() => {
    const unsub = subscribe("*", ({ event }) => {
      if (event === "notification.new" && open) load();
    });
    return unsub;
  }, [subscribe, open, load]);

  const markOne = async (n) => {
    if (n.read) return;
    try {
      await api.patch(`/notifications/${n.id}/read`);
      await load();
    } catch (err) { if (typeof console !== 'undefined') console.debug('components/NotificationDrawer.jsx: swallowed', err); }
  };

  const markAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/notifications/mark-all-read");
      toast.success(`${data.updated} marked as read`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed"); }
    setBusy(false);
  };

  const remove = async (n) => {
    try {
      await api.delete(`/notifications/${n.id}`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed"); }
  };

  const clearRead = async () => {
    if (!window.confirm("Delete all read notifications?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/notifications/delete",
        { delete_read: true });
      toast.success(`${data.deleted} deleted`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed"); }
    setBusy(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="glass-dark border-l border-white/10 w-full sm:max-w-md p-0 flex flex-col h-full"
                     data-testid="notification-drawer">
        <SheetHeader className="p-4 border-b border-white/10 shrink-0">
          <SheetTitle className="text-white font-display flex items-center gap-2">
            <Bell className="h-4 w-4 text-orange-400" /> Notifications
            {unread > 0 && (
              <Badge variant="outline" className="border-orange-500/40 text-orange-300"
                     data-testid="notif-drawer-unread">{unread} unread</Badge>
            )}
          </SheetTitle>
          <SheetDescription className="sr-only">
            Alerts, RFP updates, board digests, and payment reminders live here. Click any item to mark it read.
          </SheetDescription>
        </SheetHeader>

        <div className="px-4 pt-2 pb-3 border-b border-white/5 flex items-center gap-2">
          <div className="flex rounded-full border border-white/10 bg-white/[0.03] p-0.5">
            <button
              onClick={() => setTab("all")}
              className={"px-3 py-1 text-xs rounded-full transition " +
                (tab === "all" ? "bg-white/10 text-white" : "text-zinc-400 hover:text-zinc-200")}
              data-testid="notif-tab-all">All</button>
            <button
              onClick={() => setTab("unread")}
              className={"px-3 py-1 text-xs rounded-full transition " +
                (tab === "unread" ? "bg-white/10 text-white" : "text-zinc-400 hover:text-zinc-200")}
              data-testid="notif-tab-unread">Unread</button>
          </div>
          <div className="ml-auto flex items-center gap-1">
            <Button size="sm" variant="outline" onClick={markAll} disabled={busy || unread === 0}
                    className="border-white/10 text-xs rounded-full" data-testid="notif-mark-all">
              <CheckCheck className="h-3 w-3 mr-1" /> Mark all read
            </Button>
            <Button size="sm" variant="ghost" onClick={clearRead} disabled={busy}
                    className="text-zinc-400 hover:text-red-300 text-xs rounded-full" data-testid="notif-clear-read">
              <Trash2 className="h-3 w-3 mr-1" /> Clear read
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-2 scroll-slim">
          {items.length === 0 ? (
            <div className="text-center py-16 text-zinc-500" data-testid="notif-empty">
              <Inbox className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <div className="text-sm">You&apos;re all caught up.</div>
            </div>
          ) : items.map((n) => (
            <div key={n.id}
                 onClick={() => markOne(n)}
                 className={"rounded-md border p-3 cursor-pointer transition " +
                   (n.read
                     ? "border-white/5 bg-white/[0.01] hover:bg-white/[0.03]"
                     : "border-orange-500/25 bg-orange-500/[0.04] hover:bg-orange-500/[0.08]")}
                 data-testid={`notif-item-${n.id}`}>
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Badge variant="outline" className={TYPE_TONE[n.type] || TYPE_TONE.default}>
                      {n.type?.replaceAll("_", " ") || "notice"}
                    </Badge>
                    {!n.read && <span className="h-1.5 w-1.5 rounded-full bg-orange-500"
                                       data-testid={`notif-unread-dot-${n.id}`} />}
                    <div className="text-xs text-zinc-500 ml-auto tabular-nums">
                      {new Date(n.created_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="text-sm text-zinc-100 font-medium mt-1">{n.title}</div>
                  {n.body && (
                    <div className="text-xs text-zinc-400 mt-0.5 whitespace-pre-wrap line-clamp-3">{n.body}</div>
                  )}
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); remove(n); }}
                  className="text-zinc-500 hover:text-red-300 shrink-0"
                  data-testid={`notif-delete-${n.id}`}>
                  <X className="h-3 w-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default NotificationDrawer;
